import { useEffect, useMemo, useState } from "react";
import { Save, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isAxiosError } from "axios";
import { getNamespace, getSectionLabels, groupFieldsBySection } from "@endeks/shared";
import { buildMirrorPayloads } from "./save-payloads";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useContentNamespaces, useContentEntry, useUpsertContent } from "./queries";
import { useContentStore } from "../../stores/content";
import { contentApi } from "../../api/endpoints";
import { FieldRenderer } from "../../components/Content/FieldRenderer";

export default function ContentEditor() {
  const { data: namespaces, isLoading: nsLoading } = useContentNamespaces();
  const [activePage, setActivePage] = useState<string>("");
  const { activeLocale, setActiveLocale, dirty, setDirty } = useContentStore();
  const upsert = useUpsertContent();

  const { data: entry, isLoading: entryLoading } = useContentEntry(activePage, activeLocale);
  const [values, setValues] = useState<Record<string, string>>({});

  // İlk namespace'i otomatik seç.
  useEffect(() => {
    if (!activePage && namespaces && namespaces.length > 0) {
      setActivePage(namespaces[0].page);
    }
  }, [namespaces, activePage]);

  // Sunucudan gelen veriyi forma yükle.
  useEffect(() => {
    if (entry) {
      setValues(entry.data ?? {});
      setDirty(false);
    }
  }, [entry, setDirty]);

  const nsDef = useMemo(() => (activePage ? getNamespace(activePage) : undefined), [activePage]);

  const imageKeys = useMemo(
    () =>
      nsDef
        ? Object.entries(nsDef.fields)
            .filter(([, f]) => f.type === "image")
            .map(([k]) => k)
        : [],
    [nsDef],
  );

  const sectionLabels = useMemo(
    () => (activePage ? getSectionLabels(activePage) : undefined),
    [activePage],
  );

  const imageGroups = useMemo(() => {
    if (!nsDef) return [];
    const imgFields = Object.fromEntries(
      Object.entries(nsDef.fields).filter(([, f]) => f.type === "image"),
    );
    return groupFieldsBySection(imgFields, sectionLabels);
  }, [nsDef, sectionLabels]);

  const textGroups = useMemo(() => {
    if (!nsDef) return [];
    const txtFields = Object.fromEntries(
      Object.entries(nsDef.fields).filter(([, f]) => f.type !== "image"),
    );
    return groupFieldsBySection(txtFields, sectionLabels);
  }, [nsDef, sectionLabels]);

  // Locale tab'larını yalnız metin alanı varken göster; namespace tamamen görselse
  // (örn. layout) tab'lar gereksiz — görseller zaten tab dışında. Boş namespace'te
  // "alan yok" mesajını göstermek için tab'ı yine render et.
  const showLocaleTabs = !nsDef || textGroups.length > 0 || Object.keys(nsDef.fields).length === 0;

  const grouped = useMemo(() => {
    const groups = [
      { key: "pages", label: "Sayfalar" },
      { key: "common", label: "Ortak Bloklar" },
    ];
    return groups.map((g) => ({
      key: g.key,
      label: g.label,
      items: (namespaces ?? [])
        .filter((n) => n.group === g.key)
        .map((n) => ({ page: n.page, label: n.label })),
    }));
  }, [namespaces]);

  const setField = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setDirty(true);
  };

  const handlePageChange = (page: string) => {
    if (dirty && !window.confirm("Kaydedilmemiş değişiklikler var. Devam edilsin mi?")) return;
    setActivePage(page);
  };

  const handleLocaleChange = (locale: string) => {
    if (dirty && !window.confirm("Kaydedilmemiş değişiklikler var. Dil değişsin mi?")) return;
    setActiveLocale(locale as "tr" | "en");
  };

  // EN sekmesinde boş alanları TR içeriğiyle doldurur (yalnız manuel tetiklenir).
  const copyFromTr = async () => {
    const { data } = await contentApi.getEntry(activePage, "tr");
    setValues((prev) => {
      const next = { ...prev };
      for (const [k, v] of Object.entries(data.data)) {
        if (!next[k]) next[k] = v;
      }
      return next;
    });
    setDirty(true);
    toast.info("Boş alanlar TR içeriğiyle dolduruldu");
  };

  const onSave = async () => {
    try {
      let otherLocaleData: Record<string, string> = {};
      if (imageKeys.length > 0) {
        const other = activeLocale === "tr" ? "en" : "tr";
        const { data } = await contentApi.getEntry(activePage, other);
        otherLocaleData = data.data ?? {};
      }
      const payloads = buildMirrorPayloads({
        activeLocale,
        values,
        imageKeys,
        otherLocaleData,
      });
      for (const p of payloads) {
        await upsert.mutateAsync({ page: activePage, locale: p.locale, data: p.data });
      }
      toast.success(`${nsDef?.label ?? activePage} (${activeLocale.toUpperCase()}) yayınlandı`);
      setDirty(false);
    } catch (e: unknown) {
      toast.error(
        isAxiosError(e) ? (e.response?.data?.message ?? "Kayıt başarısız") : "Kayıt başarısız",
      );
    }
  };

  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">İçerik Yönetimi</h1>
        <Button onClick={onSave} disabled={!activePage || upsert.isPending}>
          {upsert.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Kaydet &amp; Yayınla
        </Button>
      </div>

      <div className="flex gap-4">
        {/* Left sidebar: page selector */}
        <div className="w-56 shrink-0">
          <div className="rounded-md border border-border bg-card overflow-hidden">
            {nsLoading ? (
              <div className="p-3 space-y-2">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-7 w-full" />
                ))}
              </div>
            ) : (
              <nav className="py-1">
                {grouped.map((group) =>
                  group.items.length === 0 ? null : (
                    <div key={group.key}>
                      <p className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">
                        {group.label}
                      </p>
                      {group.items.map((item) => (
                        <button
                          key={item.page}
                          onClick={() => handlePageChange(item.page)}
                          className={cn(
                            "w-full text-left px-3 py-2 text-sm transition-colors hover:bg-accent hover:text-accent-foreground",
                            activePage === item.page
                              ? "bg-accent text-accent-foreground font-medium"
                              : "text-foreground",
                          )}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  ),
                )}
              </nav>
            )}
          </div>
        </div>

        {/* Right: editor */}
        <div className="flex-1 min-w-0">
          <div className="rounded-md border border-border bg-card p-4">
            {!entryLoading && nsDef && imageGroups.length > 0 && (
              <div className="space-y-5 mb-6">
                {imageGroups.map((group) => (
                  <div key={group.sectionId ?? "genel"} className="space-y-3">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {group.label}
                    </h3>
                    {group.fields.map(({ key, field }) => (
                      <div key={key} className="space-y-1.5">
                        <Label>{field.label}</Label>
                        <FieldRenderer field={field} value={values[key] ?? ""} onChange={(v) => setField(key, v)} />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {showLocaleTabs && (
            <Tabs value={activeLocale} onValueChange={handleLocaleChange}>
              <div className="flex items-center justify-between mb-4">
                <TabsList>
                  <TabsTrigger value="tr">Türkçe</TabsTrigger>
                  <TabsTrigger value="en">English</TabsTrigger>
                </TabsList>
                {activeLocale === "en" && (
                  <Button type="button" variant="outline" size="sm" onClick={copyFromTr}>
                    <Copy className="h-3.5 w-3.5" />
                    TR'den kopyala
                  </Button>
                )}
              </div>

              {["tr", "en"].map((locale) => (
                <TabsContent key={locale} value={locale}>
                  {entryLoading || !nsDef ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : Object.keys(nsDef.fields).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4">Bu blokta alan yok.</p>
                  ) : (
                    <div className="space-y-6">
                      {textGroups.map((group) => (
                        <div key={group.sectionId ?? "genel"} className="space-y-5">
                          {textGroups.length > 1 && (
                            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                              {group.label}
                            </h3>
                          )}
                          {group.fields.map(({ key, field }) => (
                            <div key={key} className="space-y-1.5">
                              <Label>{field.label}</Label>
                              <FieldRenderer
                                field={field}
                                value={values[key] ?? ""}
                                onChange={(v) => setField(key, v)}
                              />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
